import React from 'react';
import ReactDOM from 'react-dom';
import { Provider } from 'react-redux';
import axios from 'axios';
import './styles/index.scss';
import { ThemeProvider } from 'styled-components';
import ApolloClinet from 'apollo-client';
import { InMemoryCache } from 'apollo-cache-inmemory';
import { createHttpLink } from 'apollo-link-http';
import { ApolloProvider } from '@apollo/react-hooks';
import { ApolloLink } from 'apollo-link';
import { ToastContainer } from 'react-toastify';
import * as Sentry from '@sentry/react';
import theme from './theme';
import * as serviceWorker from './serviceWorker';
import Routes from './config/routes';
import Auth from './utils/Auth';
import { setTitle, setFavicon } from './brand';
import { isLocal, getBackendBaseUrl, apiRouts } from './constants';
import store from './config/store';
import 'react-toastify/dist/ReactToastify.css';
import 'bootstrap/dist/css/bootstrap.min.css';

export const releaseVersion = !process.env.REACT_APP_RELEASE_VERSION
  ? 'dev'
  : process.env.REACT_APP_RELEASE_VERSION;
export const environ = process.env.REACT_APP_PREFIX.split('_')[1];
export const environUri = environ === 'prod' ? '' : `${environ}.`;

const context = {};
window['reactContext'] = context;
context['isLocal'] = isLocal;
context['apiRoutes'] = apiRouts;

setTitle();
setFavicon();

if (!isLocal) {
    try {
        Sentry.init({
          dsn: 'https://67ffdaf7c20c468ba4dd5e2e4deb071e@o363148.ingest.sentry.io/5901447',
          integrations: [
            Sentry.replayIntegration({
                maskAllText: false,
                maskAllInputs: environ === 'prod' || environ === 'staging',
                useCompression: true,
                blockAllMedia: false
            }),
            Sentry.browserTracingIntegration()
          ],
          initialScope: {
            tags: {
              environment: environ
            }
          },
          replaysSessionSampleRate: environ === 'prod' ? 1 : 0.1,
          replaysOnErrorSampleRate: environ === 'prod' ? 1 : 0.1,
          tracesSampleRate: environ === 'prod' ? 1 : 0.1,
          sampleRate: environ === 'prod' ? 1 : 0.1,
          release: releaseVersion,
          environment: environ,
          ignoreErrors: [
            'Non-Error promise rejection captured',
            'ResizeObserver loop limit exceeded',
            'ResizeObserver loop completed with undelivered notifications.'
          ],
          beforeSend(event, hint) {
            // Check if it is an exception, and if so, show the report dialog
            if (event.exception && process.env.REACT_APP_SENTRY_DIALOG) {
              Sentry.showReportDialog({ eventId: event.event_id, labelSubmit: 'Submit this Report' });
            }
            return event;
          }
        });
    } catch (e) {
      // do nothing
    }
}


// axios.defaults.baseURL = 'https://backend.' + window.location.hostname + '/api';
axios.defaults.baseURL = getBackendBaseUrl();

const chatUrl = isLocal ? process.env.REACT_APP_GRQPHQL_HOST
    : 'https://graphql.chat.' + window.location.hostname + '/graphql';

const httpLink = createHttpLink({
    uri: chatUrl
});

const middlewareLink = new ApolloLink((operation, forward) => {
    const token = Auth.getInstance().getAuthToken();
    operation.setContext({
        headers: {
            Authorization: token ? `${token}` : ''
            // 'X-Api-Key': process.env.REACT_APP_GRQPHQL_API_KEY
        }
    });
    return forward(operation);
});

// use with apollo-client
const link = middlewareLink.concat(httpLink);

const client = new ApolloClinet({
    link,
    cache: new InMemoryCache()
});

class ErrorHandler extends React.Component {
    componentDidCatch(error, info) {
        // send email or send xray, etc
        // console.log('Caught error - ' + error + ' - ' + info);
    }

    render() {
        return this.props.children;
    }
}

ReactDOM.render(
    // @ts-ignore
    <ApolloProvider client={client}>
        <Provider store={store}>
            <ThemeProvider theme={theme}>
                <ErrorHandler>
                    <Routes />
                    <ToastContainer autoClose={2000} draggablePercent={60} />
                </ErrorHandler>
            </ThemeProvider>
        </Provider>
    </ApolloProvider>,
    document.getElementById('root'),
);


// If you want your app to work offline and load faster, you can change
// unregister() to register() below. Note this comes with some pitfalls.
// Learn more about service workers: http://bit.ly/CRA-PWA
serviceWorker.unregister();

// build trigger 1
